import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './use-auth';
import { useToast } from '@/hooks/use-toast';

interface CreateFAQData {
  question: string;
  answer: string;
  category_id?: string;
  is_published?: boolean;
  sort_order?: number;
}

interface UpdateFAQData {
  question?: string;
  answer?: string;
  category_id?: string;
  is_published?: boolean;
  sort_order?: number;
}

export function useSupportFAQs() {
  return useQuery({
    queryKey: ['support-faqs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_faqs')
        .select(`
          *,
          support_categories (
            id,
            name
          )
        `)
        .eq('is_published', true)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useAllSupportFAQs() {
  return useQuery({
    queryKey: ['support-faqs-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('support_faqs')
        .select(`
          *,
          support_categories (
            id,
            name
          ),
          created_by_user:profiles!support_faqs_created_by_fkey (
            full_name
          ),
          updated_by_user:profiles!support_faqs_updated_by_fkey (
            full_name
          )
        `)
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function useCreateSupportFAQ() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateFAQData) => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('support_faqs')
        .insert({
          ...data,
          created_by: user.id,
          updated_by: user.id,
        });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-faqs'] });
      queryClient.invalidateQueries({ queryKey: ['support-faqs-all'] });
      toast({
        title: "FAQ criado",
        description: "FAQ criado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao criar FAQ",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useUpdateSupportFAQ(faqId: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: UpdateFAQData) => {
      if (!user) throw new Error('User not authenticated');

      const { error } = await supabase
        .from('support_faqs')
        .update({
          ...data,
          updated_by: user.id,
        })
        .eq('id', faqId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-faqs'] });
      queryClient.invalidateQueries({ queryKey: ['support-faqs-all'] });
      toast({
        title: "FAQ atualizado",
        description: "FAQ atualizado com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao atualizar FAQ",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}

export function useDeleteSupportFAQ() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (faqId: string) => {
      const { error } = await supabase
        .from('support_faqs')
        .delete()
        .eq('id', faqId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-faqs'] });
      queryClient.invalidateQueries({ queryKey: ['support-faqs-all'] });
      toast({
        title: "FAQ excluído",
        description: "FAQ excluído com sucesso!",
      });
    },
    onError: (error) => {
      toast({
        title: "Erro ao excluir FAQ",
        description: error.message,
        variant: "destructive",
      });
    },
  });
}